import { useCallback, useEffect, useState } from 'react';
import { MessageSquareWarning, ShieldAlert } from 'lucide-react';
import { api } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import NotaExplicativa from '../ui/NotaExplicativa';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import { semaforoReclamo, queHacer } from '../ui/reclamos';

// RECLAMOS DE CLIENTE · Fase 3 del despegue.
//
// Esta pantalla existe para un momento con prisa: acaba de llamar un
// cliente y hay un reloj corriendo. Por eso el formulario pide lo mínimo
// —quién y qué pasó— y todo lo demás se puede completar después: exigir
// el número de parte en la primera llamada haría que el reclamo se
// apunte media hora más tarde, con el plazo ya comido.
export default function Reclamos({ irA }) {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');
  const [clientes, setClientes] = useState([]);
  const [creando, setCreando] = useState(false);
  const [conteniendo, setConteniendo] = useState(null);
  const [aviso, setAviso] = useState('');

  const cargar = useCallback(
    () => api.get('/reclamos').then(setFilas).catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/clientes').then((d) => setClientes(d.clientes || d || []))
      .catch(() => {});
  }, []);

  const crear = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setError('');
    try {
      const d = await api.post('/reclamos', {
        id_cliente: Number(f.get('id_cliente')),
        numero_reclamo: f.get('numero_reclamo') || null,
        sku: f.get('sku') || null,
        cantidad_afectada: Number(f.get('cantidad_afectada')),
        descripcion: f.get('descripcion'),
      });
      setCreando(false);
      setAviso(d.mensaje);
      cargar();
    } catch (err) { setError(err.message); }
  };

  const contener = async (e) => {
    e.preventDefault();
    const texto = new FormData(e.target).get('texto');
    try {
      const d = await api.post(`/reclamos/${conteniendo.id}/contencion`, { texto });
      setConteniendo(null);
      setAviso(d.mensaje);
      cargar();
    } catch (err) { setError(err.message); }
  };

  return (
    <>
      <ListaEstandar
        titulo="Reclamos de cliente"
        subtitulo="Lo que un cliente reclamó, con su número y el reloj de la contención corriendo."
        nota={
          <NotaExplicativa titulo="Qué es contener, y por qué corre prisa">
            Cuando un cliente reclama, lo primero <strong>no es
            analizar</strong>: es <strong>contener</strong> — revisar el
            material que va en camino, apartar lo que quede en almacén y
            avisar a quien esté por embarcar. Las armadoras dan 24 horas
            para eso. Aquí se registra qué se hizo; el análisis y las
            acciones van en la no conformidad que nace con el reclamo.
          </NotaExplicativa>
        }
        cargando={filas === null}
        error={error}
        accionPrincipal={
          <button type="button" onClick={() => setCreando(true)}
                  className="inline-flex items-center gap-2 rounded-lg
                             bg-[var(--color-marca)] px-3 py-2 text-sm
                             font-semibold text-white">
            <MessageSquareWarning className="h-4 w-4" aria-hidden="true" />
            Registrar un reclamo
          </button>
        }
        vacio={
          <EstadoVacio
            icono={MessageSquareWarning}
            titulo="Ningún cliente ha reclamado"
            descripcion="Cuando llegue uno, apúntalo aquí en cuanto cuelgues el teléfono: al registrarlo nace su no conformidad y empieza a correr el plazo de contención."
          />
        }
        columnas={[
          { etiqueta: 'Folio', valor: (f) => f.folio },
          { etiqueta: 'Cliente', valor: (f) => f.cliente },
          { etiqueta: 'Su número', valor: (f) => f.numero_reclamo || '—' },
          { etiqueta: 'Parte', valor: (f) => f.numero_parte || f.sku || '—' },
          { etiqueta: 'Piezas', valor: (f) => f.cantidad_afectada,
            alineado: 'derecha' },
          { etiqueta: 'Contención',
            valor: (f) => semaforoReclamo(f.semaforo).etiqueta,
            render: (f) => <ChipEstado {...semaforoReclamo(f.semaforo)} /> },
        ]}
        filas={filas || []}
        acciones={(f) => [
          ...(f.contencion_en ? [] : [{
            etiqueta: 'Registrar contención',
            onClick: () => setConteniendo(f),
          }]),
          ...(f.id_no_conformidad ? [{
            etiqueta: `Ver 8D ${f.folio_no_conformidad}`,
            onClick: () => irA?.('no-conformidades',
                                 { id: f.id_no_conformidad }),
          }] : []),
        ]}
      />

      {aviso && (
        <div role="status"
             className="mt-3 rounded-lg border border-amber-300 bg-amber-50
                        p-3 text-sm text-amber-900">
          {aviso}{' '}
          <button type="button" onClick={() => setAviso('')}
                  className="font-semibold underline">Entendido</button>
        </div>
      )}

      {creando && (
        <Modal titulo="Registrar un reclamo" onCerrar={() => setCreando(false)}>
          <form onSubmit={crear} className="flex flex-col gap-3">
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Cliente</span>
              <select name="id_cliente" required
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                <option value="">Elige…</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Su número de reclamo</span>
              <input name="numero_reclamo"
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <span className="text-gray-500">
                El que te dio el cliente. Es con el que va a preguntar.
              </span>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Producto (si lo dijo)</span>
              <input name="sku"
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <span className="text-gray-500">
                Puedes dejarlo en blanco y completarlo después: no vale la
                pena retrasar el reclamo por averiguarlo.
              </span>
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Cuántas piezas</span>
              <input name="cantidad_afectada" type="number" step="any" min="0.001" required
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Qué dijo el cliente</span>
              <textarea name="descripcion" required minLength={5} rows={3}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>
            <button type="submit"
                    className="mt-1 rounded-lg bg-[var(--color-marca)] px-3 py-2
                               text-sm font-semibold text-white">
              Registrar y arrancar el reloj
            </button>
          </form>
        </Modal>
      )}

      {conteniendo && (
        <Modal titulo={`Contención del reclamo ${conteniendo.folio}`}
               onCerrar={() => setConteniendo(null)}>
          <p className="flex items-start gap-2 rounded-lg border border-amber-300
                        bg-amber-50 p-3 text-sm text-amber-900">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{queHacer(conteniendo)}</span>
          </p>
          <form onSubmit={contener} className="mt-3 flex flex-col gap-3">
            <label className="text-sm">
              <span className="font-semibold text-gray-800">Qué se hizo</span>
              <textarea name="texto" required minLength={10} rows={4}
                        placeholder="Se detuvo el embarque de hoy, se apartaron 3 tarimas en PT-01 y se avisó a producción."
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <span className="text-gray-500">
                Esto se enseña delante del cliente. Tres líneas concretas
                valen más que un párrafo general — y no se puede cambiar
                después: lo que venga luego va en el 8D.
              </span>
            </label>
            <button type="submit"
                    className="rounded-lg bg-[var(--color-marca)] px-3 py-2
                               text-sm font-semibold text-white">
              Registrar la contención
            </button>
          </form>
        </Modal>
      )}
    </>
  );
}
